import React, { useState, useRef, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Plus, MoreVertical, Trash2, ArrowLeft } from 'lucide-react';

const ChatHistory = ({
    conversations,
    currentConversationId,
    onSelectConversation,
    selectedAgent,
    showChatHistory,
    isLoading,
    handleNewConversation,
    onDeleteConversation,
    handleBackToAgents
}) => {
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpenDropdownId(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const truncateMessage = (message, maxLength = 45) => {
        if (!message) return 'No messages yet';
        return message.length > maxLength
            ? message.substring(0, maxLength) + '...'
            : message;
    };

    const getLastUserMessage = (messages) => {
        if (!messages || messages.length === 0) return null;
        // Find the last user message to show as preview
        for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i].role === 'user') {
                return messages[i];
            }
        }
        return messages[messages.length - 1]; // Fallback to last message
    };

    const getConversationTitle = (conversation) => {
        return conversation.title;
    };

    const handleDropdownClick = (e, conversationId) => {
        e.stopPropagation(); // Prevent conversation selection
        setOpenDropdownId(openDropdownId === conversationId ? null : conversationId);
    };

    const handleDeleteClick = (e, conversation) => {
        e.stopPropagation(); // Prevent conversation selection
        setOpenDropdownId(null); // Close dropdown
        // if (window.confirm('Are you sure you want to delete this conversation? This action cannot be undone.')) {
        onDeleteConversation(conversation);
        // }
    };

    return (
        <div className={`${showChatHistory ? '' : 'hidden md:block'}  min-w-full md:min-w-[200px] md:max-w-[200px] bg-gray-50 border-r border-gray-200 h-full flex flex-col`}>
            {/* Fixed Header */}
            <div className="p-2 border-b border-gray-200 bg-purple-50 flex-shrink-0">
                <div className='flex flex-row items-center justify-between'>
                    <div className='flex flex-row items-center justify-start gap-x-1'>
                        {/* 
                         Remove this button for now, because we want the use to just go back to the landing page
                        <button
                            onClick={handleBackToAgents}
                            className="block md:hidden  mr-1 md:mr-3 text-gray-500 hover:text-gray-700 focus:outline-none"
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </button> */}
                        <h3 className="font-semibold text-gray-800 flex items-center">
                            {/* <div className="w-2 h-2 bg-purple-600 rounded-full mr-2"></div> */}
                            Chat History
                        </h3>
                    </div>
                    {/* Show New Chat button when: no conversations OR all conversations have messages */}
                    {(conversations.length === 0 || conversations.every(conv => conv.messages && conv.messages.length > 0)) && (
                        <button
                            onClick={handleNewConversation}
                            className="md:hidden text-purple-900 flex items-center gap-1 px-2 md:px-3 py-1 md:py-2 text-sm rounded-md xhover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                            title="New Conversation"
                            disabled={isLoading}
                        >
                            <Plus className="w-4 h-4" />
                            New Chat
                        </button>
                    )}
                </div>
                {conversations.length > 0 && (
                    <p className="text-xs text-gray-600 mt-1">
                        {conversations.length} conversation{conversations.length !== 1 ? 's' : ''}
                    </p>
                )}
                {conversations.length == 0 && (
                    <p className="text-xs text-gray-600 mt-1">
                        &nbsp;
                    </p>
                )}
            </div>

            {/* Scrollable Content  h-[100vh] md:h-[calc(100vh-10rem)] lg:max-h-[800px]  */}
            <div className="flex-1 overflow-y-auto max-h-[calc(100vh-100px)] md:h-[calc(100vh-10rem)] "   >
                <div className="divide-y divide-gray-200">
                    {conversations.length === 0 ? (
                        <div className="p-4 text-center text-gray-500">
                            <div className="text-sm">No conversations yet</div>
                            <div className="text-xs mt-1">Start chatting to see your history</div>
                        </div>
                    ) : (
                        conversations.map((conversation) => {
                            const lastMessage = getLastUserMessage(conversation.messages);
                            const isActive = conversation._id === currentConversationId;
                            const conversationTitle = getConversationTitle(conversation);
                            const hasMessages = conversation.messages && conversation.messages.length > 0;
                            const isDropdownOpen = openDropdownId === conversation._id;

                            return (
                                <div
                                    key={conversation._id}
                                    className={`relative transition-colors duration-200 ${isActive ? 'bg-purple-100 !border-r-4 !border-purple-600 !border-t-0' : 'hover:bg-gray-100'}`}
                                >
                                    <div className="flex items-center justify-between">


                                        {/* Conversation content */}
                                        <button
                                            onClick={() => onSelectConversation(conversation)}
                                            className="flex-1 p-2 text-left hover:bg-purple-50 transition-colors duration-200"
                                        >
                                            <div className="flex flex-col space-y-2">
                                                <div className="flex items-start justify-between gap-1">
                                                    <div className="flex items-start space-x-2 min-w-0 flex-1">
                                                        <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5 ${isActive ? 'bg-purple-600' : hasMessages ? 'bg-gray-400' : 'bg-gray-300'
                                                            }`}></div>
                                                        <p className={`text-xs leading-relaxed ${isActive ? 'text-purple-700' : 'text-gray-600'
                                                            }`}>
                                                            {hasMessages ? conversationTitle : 'new conversation'}
                                                        </p>
                                                    </div>
                                                </div>
                                                {lastMessage &&
                                                    <span className="text-xs text-gray-500 pl-4">
                                                        {formatDistanceToNow(new Date(lastMessage.timestamp), { addSuffix: true })}
                                                    </span>
                                                }
                                            </div>
                                        </button>


                                        {/* Dropdown button - only show for conversations with messages */}
                                        {/* {hasMessages && ( */}
                                            <div className="relative" ref={isDropdownOpen ? dropdownRef : null}>
                                                <button
                                                    onClick={(e) => handleDropdownClick(e, conversation._id)}
                                                    className="p-2 hover:bg-gray-200 transition-colors duration-200 rounded-sm"
                                                    title="More options"
                                                >
                                                    <MoreVertical className="w-3 h-3 text-gray-400" />
                                                </button>

                                                {/* Dropdown menu */}
                                                {isDropdownOpen && (
                                                    <div className="absolute -left-28 top-full mt-1 w-32 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                                                        <button
                                                            onClick={(e) => handleDeleteClick(e, conversation)}
                                                            className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 rounded-md"
                                                        >
                                                            <Trash2 className="w-3 h-3" />
                                                            Delete
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        {/* )} */}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChatHistory; 